import React, { useState } from 'react';
import axios from '../axiosConfig';
import { Container as MapDiv, NaverMap, useNavermaps } from 'react-naver-maps';
import SoilTypeMarker from './SoilTypeMarker';
import SoilTypeLegend from './SoilTypeLegend';
import LiquefactionAssessmentMarker from './LiquefactionAssessmentMarker';
import LiquefactionAssessmentLegend from './LiquefactionAssessmentLegend';
import Plot from 'react-plotly.js';
import USCSLabelingModal from './USCSLabelingModal';
import AnalysisProgressModal from './AnalysisProgressModal';
import AnalysisResultView from './AnalysisResultView';

/**
 * 내진설계지원 - STEP 2: 분석 화면
 * 액상화 평가 지도 + 지반 분류 지도 + SPT 그룹 차트 + 일괄 다운로드 + 지반응답해석
 */
function SeismicAnalysis({ selectedBoreholes, onBack, showToast }) {
  const navermaps = useNavermaps();
  const [graphSorting, setGraphSorting] = useState("x-asc");
  const [isDownloading, setIsDownloading] = useState(false);
  const [soilMap, setSoilMap] = useState(null);
  const [liquefactionMap, setLiquefactionMap] = useState(null);

  // 지반응답해석 관련 상태
  const [showUSCSModal, setShowUSCSModal] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [showResultView, setShowResultView] = useState(false);
  const [outputDir, setOutputDir] = useState(null);

  // 마커 분포에 맞게 지도 bounds 설정
  React.useEffect(() => {
    if (selectedBoreholes && selectedBoreholes.length > 0 && soilMap && liquefactionMap) {
      const lats = selectedBoreholes.map(b => b.grs80_x);
      const lngs = selectedBoreholes.map(b => b.grs80_y);

      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);
      const minLng = Math.min(...lngs);
      const maxLng = Math.max(...lngs);

      // LatLngBounds 객체 생성
      const bounds = new navermaps.LatLngBounds(
        new navermaps.LatLng(minLat, minLng),
        new navermaps.LatLng(maxLat, maxLng)
      );

      // 두 지도 모두에 bounds 적용 (약간의 여백 포함)
      soilMap.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
      liquefactionMap.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
    }
  }, [selectedBoreholes, navermaps, soilMap, liquefactionMap]);

  // 두 지도 동기화
  React.useEffect(() => {
    if (!soilMap || !liquefactionMap) return;

    let syncing = false;

    const syncFromSoilMap = () => {
      if (syncing) return;
      syncing = true;
      try {
        const center = soilMap.getCenter();
        const zoom = soilMap.getZoom();
        liquefactionMap.setCenter(center);
        liquefactionMap.setZoom(zoom);
      } catch (e) {
        console.error('Sync error from soil map:', e);
      }
      setTimeout(() => { syncing = false; }, 50);
    };

    const syncFromLiquefactionMap = () => {
      if (syncing) return;
      syncing = true;
      try {
        const center = liquefactionMap.getCenter();
        const zoom = liquefactionMap.getZoom();
        soilMap.setCenter(center);
        soilMap.setZoom(zoom);
      } catch (e) {
        console.error('Sync error from liquefaction map:', e);
      }
      setTimeout(() => { syncing = false; }, 50);
    };

    const listeners = [
      navermaps.Event.addListener(soilMap, 'zoom_changed', syncFromSoilMap),
      navermaps.Event.addListener(soilMap, 'dragend', syncFromSoilMap),
      navermaps.Event.addListener(liquefactionMap, 'zoom_changed', syncFromLiquefactionMap),
      navermaps.Event.addListener(liquefactionMap, 'dragend', syncFromLiquefactionMap),
    ];

    return () => {
      listeners.forEach(listener => {
        try {
          navermaps.Event.removeListener(listener);
        } catch (e) {
          console.error('Error removing listener:', e);
        }
      });
    };
  }, [soilMap, liquefactionMap, navermaps]);

  // SPT 그룹 시각화 차트 렌더링 (기존 MapView 코드 재사용)
  const renderStackedSPTGraphWithMultipleBoreholes = (boreholes, graphSorting) => {
    if (!boreholes || boreholes.length === 0) {
      return <p className="mt-4 ml-4 text-secondary-600">선택된 시추공이 없습니다.</p>;
    }

    const spectralColors = [
      '#9e0142', '#d53e4f', '#f46d43', '#fdae61', '#fee08b',
      '#e6f598', '#abdda4', '#66c2a5', '#3288bd', '#5e4fa2'
    ];

    const allBarData = [];
    const allLineData = [];
    boreholes.sort((a, b) => {
      switch(graphSorting) {
        case 'x-asc':
          return a.grs80_x - b.grs80_x;
        case 'x-desc':
          return b.grs80_x - a.grs80_x;
        case 'y-asc':
          return a.grs80_y - b.grs80_y;
        case 'y-desc':
          return b.grs80_y - a.grs80_y;
        default:
          return 0;
      }
    }).forEach((borehole, index) => {
      const sortedSpts = borehole.spts.sort((a, b) => b.elevation - a.elevation);
      const filteredSptsForBar = sortedSpts.filter(spt => spt.elevation !== null && spt.n === null && spt.d === null);
      const filteredSptsForLine = sortedSpts.filter(spt => spt.elevation !== null && spt.n !== null && spt.d !== null);

      if (filteredSptsForBar.length === 0 && filteredSptsForLine.length === 0) {
        return;
      }

      filteredSptsForBar.sort((a, b) => b.elevation - a.elevation);
      const minElevation = Math.min(...sortedSpts.map(spt => spt.elevation));

      const highValues = filteredSptsForBar.map(spt => spt.elevation);
      const lowValues = highValues.map((elev, idx) => {
        return idx === filteredSptsForBar.length - 1 ? minElevation : highValues[idx + 1];
      });

      const barData = filteredSptsForBar.map((spt, idx) => ({
        x: [index],
        y: [highValues[idx] - lowValues[idx]],
        base: [lowValues[idx]],
        type: 'bar',
        width: 0.2,
        name: `Layer ${idx + 1} (${borehole.name})`,
        marker: {
          color: spectralColors[idx % spectralColors.length],
          line: {
            color: 'black',
            width: 1.5,
          },
        },
        hovertemplate: `Borehole: ${borehole.name}<br>High: ${highValues[idx].toFixed(2)}<br>Low: ${lowValues[idx].toFixed(2)}<extra></extra>`,
      }));

      // Line Chart for SPT n 값
      const lineData = {
        x: filteredSptsForLine.map(() => index),  // Borehole을 위한 고유한 x축 값
        y: filteredSptsForLine.map(spt => spt.elevation),  // y축은 elevation 값
        mode: 'lines+markers',
        type: 'scatter',
        name: `SPT N값 (${borehole.name})`,
        marker: {
          color: 'blue',
          size: 6,
        },
        line: {
          color: 'blue',
          width: 2,
        },
        hovertemplate: `Borehole: ${borehole.name}<br>SPT-N: %{text}<br>Elevation: %{y}<extra></extra>`,
        text: filteredSptsForLine.map(spt => spt.n),  // hover 시 N값 표시
        xaxis: 'x',  // 동일한 x축 사용
      };

      // SPT N값을 0-50 범위에서 Bar 내부 상대적 위치로 변환하여 x축 상대 위치로 변환
      const transformedLineData = {
        ...lineData,
        x: filteredSptsForLine.map(spt => (index + (spt.n / 50) * 0.2 - 0.1)),  // n값을 0-50 범위에서 bar 내부 상대적 위치로 표현
      };

      allBarData.push(...barData);
      allLineData.push(transformedLineData);
    });

    const data = [...allBarData, ...allLineData];

    const layout = {
      xaxis: {
        title: 'Boreholes',
        tickvals: boreholes.map((_, idx) => idx),  // 각 Borehole의 고유한 x축 값
        ticktext: boreholes.map(borehole => borehole.name),  // 각 Borehole의 이름
        range: [-0.5, boreholes.length - 0.5],  // Borehole의 개수에 따른 x축 범위 설정
      },
      yaxis: {
        title: 'Elevation',
        showspikes: true,
        spikemode: 'across',
        spikesnap: 'cursor',
        spikethickness: 2,
        spikecolor: 'black',
      },
      barmode: 'stack',
      hovermode: 'closest',
      showlegend: false,
      margin: { l: 60, r: 30, t: 30, b: 60 },
    };

    return (
      <Plot
        data={data}
        layout={layout}
        config={{
          responsive: true,
          displayModeBar: false,  // 툴바 제거
        }}
        style={{ width: '100%', height: '100%' }}  // 너비와 높이를 100%로 설정
        useResizeHandler={true}
      />
    );
  };

  // 일괄 표준서식 다운로드
  const handleBulkDownload = async () => {
    setIsDownloading(true);
    const token = localStorage.getItem('token');
    const boreholeIds = selectedBoreholes.map(b => b.id);

    try {
      const response = await axios.post('/boreholes/export-bulk', boreholeIds, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', '표준서식_일괄다운로드.zip');
      document.body.appendChild(link);
      link.click();
      link.remove();
      showToast('표준서식 일괄 다운로드가 완료되었습니다!');
    } catch (error) {
      console.error('일괄 다운로드 실패:', error);
      showToast('일괄 다운로드에 실패했습니다.');
    } finally {
      setIsDownloading(false);
    }
  };

  // 지반응답해석 모듈 실행
  const handleGroundResponseAnalysis = () => {
    setShowUSCSModal(true);
  };

  const handleUSCSComplete = () => {
    setShowUSCSModal(false);
    setShowProgressModal(true);
  };

  const handleAnalysisComplete = (outputDirPath) => {
    setShowProgressModal(false);
    setOutputDir(outputDirPath);
    setShowResultView(true);
  };

  const handleResultViewBack = () => {
    setShowResultView(false);
    setOutputDir(null);
  };

  return (
    <div className="flex-1 overflow-auto bg-secondary-50">
      {showResultView ? (
        <AnalysisResultView
          outputDir={outputDir}
          onBack={handleResultViewBack}
          // 결과 목록에서 시추공을 눌러 상세 해석으로 내려갈 때 DB id 가 필요하다.
          boreholes={selectedBoreholes}
        />
      ) : (
        <>
          <div className="max-w-7xl mx-auto p-8">
            {/* 헤더 */}
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-secondary-900 mb-2">내진설계지원 - 분석</h1>
                <p className="text-secondary-600">
                  선택된 시추공: <span className="font-semibold text-primary-600">{selectedBoreholes.length}개</span>
                </p>
              </div>
              <button
                onClick={onBack}
                className="px-4 py-2 bg-secondary-200 hover:bg-secondary-300 text-secondary-900 rounded-xl font-semibold transition-colors"
              >
                ← 뒤로가기
              </button>
            </div>

            {/* 메인 컨텐츠 */}
            <div className="space-y-6">

          {/* 액션 버튼 */}
          <div className="bg-white rounded-2xl shadow-medium p-6">
            <h2 className="text-xl font-bold text-secondary-900 mb-4">분석 도구</h2>
            <div className="flex space-x-4">
              <button
                onClick={handleBulkDownload}
                disabled={isDownloading}
                className={`flex-1 px-6 py-3 rounded-xl font-semibold transition-all flex items-center justify-center space-x-2 ${
                  isDownloading
                    ? 'bg-secondary-300 text-secondary-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-green-600 to-green-700 text-white hover:shadow-hard'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>{isDownloading ? '다운로드 중...' : '표준서식 일괄 다운로드'}</span>
              </button>
              <button
                onClick={handleGroundResponseAnalysis}
                className="flex-1 px-6 py-3 rounded-xl font-semibold bg-gradient-to-r from-purple-600 to-purple-700 text-white hover:shadow-hard transition-all flex items-center justify-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <span>지반응답해석 모듈 실행</span>
              </button>
            </div>
          </div>

          {/* 지도 섹션 */}
          <div className="grid grid-cols-2 gap-6">
            {/* 지반 분류 지도 */}
            <div className="bg-white rounded-2xl shadow-medium p-6">
              <h2 className="text-xl font-bold text-secondary-900 mb-4">지반 분류</h2>
              <div className="relative" style={{ height: '400px' }}>
                <MapDiv style={{ width: '100%', height: '100%' }}>
                  <NaverMap
                    defaultCenter={new navermaps.LatLng(36.5, 127.5)}
                    defaultZoom={7}
                    ref={setSoilMap}
                  >
                    {selectedBoreholes.map((borehole) => (
                      <SoilTypeMarker key={borehole.id} borehole={borehole} selected={true} />
                    ))}
                  </NaverMap>
                  <SoilTypeLegend />
                </MapDiv>
              </div>
            </div>

            {/* 액상화 평가 지도 */}
            <div className="bg-white rounded-2xl shadow-medium p-6">
              <h2 className="text-xl font-bold text-secondary-900 mb-4">액상화 예비평가</h2>
              <div className="relative" style={{ height: '400px' }}>
                <MapDiv style={{ width: '100%', height: '100%' }}>
                  <NaverMap
                    defaultCenter={new navermaps.LatLng(36.5, 127.5)}
                    defaultZoom={7}
                    ref={setLiquefactionMap}
                  >
                    {selectedBoreholes.map((borehole) => (
                      <LiquefactionAssessmentMarker key={borehole.id} borehole={borehole} selected={true} />
                    ))}
                  </NaverMap>
                  <LiquefactionAssessmentLegend />
                </MapDiv>
              </div>
            </div>
          </div>

          {/* SPT 그룹 시각화 차트 */}
          <div className="bg-white rounded-2xl shadow-medium p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-secondary-900">SPT 그룹 시각화</h2>
              <select
                value={graphSorting}
                onChange={(e) => setGraphSorting(e.target.value)}
                className="px-4 py-2 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="x-asc">X 좌표 오름차순</option>
                <option value="x-desc">X 좌표 내림차순</option>
                <option value="y-asc">Y 좌표 오름차순</option>
                <option value="y-desc">Y 좌표 내림차순</option>
              </select>
            </div>
            <div style={{ width: '100%', height: '600px' }}>
              {renderStackedSPTGraphWithMultipleBoreholes(selectedBoreholes, graphSorting)}
            </div>
          </div>

            </div>
          </div>

          {/* 모달들 */}
          {showUSCSModal && (
            <USCSLabelingModal
              selectedBoreholes={selectedBoreholes.map(b => b.id)}
              onClose={() => setShowUSCSModal(false)}
              onComplete={handleUSCSComplete}
            />
          )}

          {showProgressModal && (
            <AnalysisProgressModal
              boreholeIds={selectedBoreholes.map(b => b.id)}
              onComplete={handleAnalysisComplete}
              onClose={() => setShowProgressModal(false)}
            />
          )}
        </>
      )}
    </div>
  );
}

export default SeismicAnalysis;
